import { useState } from 'react';
import { HistoryOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Empty,
  Popconfirm,
  Popover,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { EMPLOYEE_EQUIPMENT_QUERY_KEY } from '@/components/email-templates/constants';
import useEmployeeNoticesT, {
  errorText,
} from '@/components/email-templates/useEmployeeNoticesT';
import {
  resendEquipmentRequest,
  updateEquipmentItemState,
} from '@/services/equipmentRequests.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import EquipmentApprovalStrip from './EquipmentApprovalStrip';
import EquipmentFillStrip from './EquipmentFillStrip';
import EquipmentStepModal from './EquipmentStepModal';
import { answersLine, itemLabel, itemLine } from './equipmentLines';

const day = (date) =>
  date ? dayjs(date).format(DATE_FORMATS.DISPLAY_DOTS) : '';

const STATE_COLORS = {
  requested: 'default',
  assigned: 'blue',
  returned: 'green',
};

const smallButton =
  'btn-default btn-default--hover !h-6 !rounded-[50px] !px-2 !text-[11.5px]';

/** "Assigned 23.10.2026. · Dell, SN 7X2… · Marko Marković" */
function StepLine({ label, date, note, byName }) {
  return (
    <span className="block text-[12px] text-zinc-500">
      {[`${label} ${day(date)}`, note, byName].filter(Boolean).join(' · ')}
    </span>
  );
}

function ItemHistory({ history, tn }) {
  if (!history?.length) return null;
  return (
    <Popover
      trigger="click"
      title={tn('equipmentHistory')}
      content={
        <ul className="m-0 max-w-[340px] pl-4 text-[12px]">
          {history.map((entry, index) => (
            <li key={index}>
              {[
                dayjs(entry.at).format(DATE_FORMATS.DISPLAY_WITH_TIME),
                tn(`equipmentHistoryActions.${entry.action}`, {
                  date: day(entry.date),
                }),
                entry.note,
                entry.byName,
              ]
                .filter(Boolean)
                .join(' · ')}
            </li>
          ))}
        </ul>
      }
    >
      <Button
        type="text"
        size="small"
        icon={<HistoryOutlined />}
        className="!h-6 !px-1 text-zinc-500"
      />
    </Popover>
  );
}

/**
 * "Equipment" tile of the employee profile: what the employee holds now
 * (items handed over), then the requests sent so far with every item's
 * state — requested → assigned → returned — and the buttons to move it a
 * step or undo the last one. The service each item went to shows its
 * addresses on hover; older requests show the item's addresses, or the
 * request's.
 */
export default function EquipmentPanel({ equipment, employeeId, onRequest, onSendToHead }) {
  const { tn } = useEmployeeNoticesT();
  const queryClient = useQueryClient();
  const requests = equipment?.requests || [];
  const assigned = equipment?.assigned || [];
  // { request, item, action } for the date + note dialog.
  const [step, setStep] = useState(null);

  // Requests made before items had their own recipients: one address line.
  const routedPerItem = (request) =>
    [...request.items, ...request.extras].some((item) => item.sentTo?.length);

  const move = useMutation({
    mutationFn: ({ requestId, ...payload }) =>
      updateEquipmentItemState(employeeId, requestId, payload),
    onSuccess: (result) => {
      message.success(result?.message || tn('equipmentItemUpdated'));
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId],
      });
      setStep(null);
    },
    onError: (error) =>
      message.error(errorText(error, tn('equipmentUpdateFailed'))),
  });

  // An approved own request whose service emails did not all go out.
  const resend = useMutation({
    mutationFn: (requestId) => resendEquipmentRequest(employeeId, requestId),
    onSuccess: (result) => {
      message.success(result?.message || tn('equipmentSent'));
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId],
      });
    },
    onError: (error) => message.error(errorText(error, tn('sendFailed'))),
  });

  const actions = (request, item) => {
    const state = item.state || 'requested';
    return (
      <span className="flex flex-wrap gap-1">
        {state === 'requested' && (
          <Button
            className={smallButton}
            onClick={() => setStep({ request, item, action: 'assign' })}
          >
            {tn('markAssigned')}
          </Button>
        )}
        {state === 'assigned' && (
          <Button
            className={smallButton}
            onClick={() => setStep({ request, item, action: 'return' })}
          >
            {tn('markReturned')}
          </Button>
        )}
        {state !== 'requested' && (
          <Popconfirm
            okButtonProps={{ className: '!rounded-[50px]' }}
            cancelButtonProps={{ className: '!rounded-[50px]' }}
            title={tn(
              state === 'returned' ? 'undoReturnConfirm' : 'undoAssignConfirm'
            )}
            okText={tn('undoStep')}
            cancelText={tn('cancel')}
            onConfirm={() =>
              move.mutateAsync({
                requestId: request._id,
                item: item.name,
                group: item.group || '',
                action: 'undo',
              })
            }
          >
            <Button className={smallButton} loading={move.isPending}>
              {tn('undoStep')}
            </Button>
          </Popconfirm>
        )}
      </span>
    );
  };

  return (
    <div className="grid gap-3">
      <EquipmentApprovalStrip
        awaiting={equipment?.awaiting || []}
        employeeId={employeeId}
      />
      <EquipmentFillStrip
        fill={equipment?.fill}
        employeeId={employeeId}
        onFill={onRequest}
      />
      {equipment?.enabled && (
        <div className="flex flex-wrap gap-2">
          {equipment.fill?.canSend && (
            <Button
              className="btn-default btn-default--hover !rounded-[50px]"
              onClick={onSendToHead}
            >
              {tn('equipmentFill.send')}
            </Button>
          )}
          {/* A head with a request waiting fills it in from the strip above. */}
          {!(equipment.fill?.open && equipment.fill?.isHead) && (
            <Button
              className="btn-default btn-default--hover !rounded-[50px]"
              onClick={onRequest}
            >
              {tn('requestEquipment')}
            </Button>
          )}
        </div>
      )}

      {requests.length > 0 && (
        <section className="grid gap-1.5 rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-[13px]">
          <h3 className="m-0 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-500">
            {tn('currentlyAssigned')}
          </h3>
          {assigned.length === 0 ? (
            <span className="text-zinc-500">{tn('nothingAssigned')}</span>
          ) : (
            <ul className="m-0 pl-4">
              {assigned.map((item) => (
                <li key={`${item.requestId}-${itemLabel(item)}`}>
                  <span className="font-semibold text-zinc-900">
                    {itemLine(item)}
                  </span>
                  <StepLine
                    label={tn('assignedOn')}
                    date={item.assignedAt}
                    note={item.assignedNote}
                    byName={item.assignedByName}
                  />
                  {item.returnRequestedAt && (
                    <span className="block text-[12px] font-semibold text-amber-700">
                      {tn('returnRequestedOn', {
                        date: day(item.returnRequestedAt),
                      })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {requests.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={tn('noEquipmentRequests')}
        />
      ) : (
        requests.map((request) => (
          <section
            key={request._id}
            className="grid gap-1.5 rounded-xl border border-zinc-200 bg-white p-3 text-[13px]"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-bold text-zinc-900">
                {dayjs(request.requestedAt).format(DATE_FORMATS.DISPLAY)}
              </span>
              <span className="text-[12px] text-zinc-500">
                {tn('requestedBy', { name: request.requestedByName || '—' })}
                {request.approval?.decidedByName &&
                  request.approval.level !== 'self' &&
                  ` · ${tn('approvedBy', { name: request.approval.decidedByName })}`}
              </span>
            </div>
            {request.notSent && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-[12px] text-red-700">
                <span>
                  {tn('equipmentNotSent', {
                    items: (request.unsentItems || []).join(', '),
                  })}
                </span>
                <Button
                  className={smallButton}
                  loading={resend.isPending}
                  onClick={() => resend.mutate(request._id)}
                >
                  {tn('equipmentResend')}
                </Button>
              </div>
            )}
            <ul className="m-0 grid list-none gap-2 p-0">
              {[...request.items, ...request.extras].map((item) => {
                const state = item.state || 'requested';
                return (
                  <li
                    key={itemLabel(item)}
                    className="flex flex-wrap items-start justify-between gap-2 border-t border-zinc-100 pt-2 first:border-t-0 first:pt-0"
                  >
                    <span className="min-w-0 flex-1">
                      <Tag color={STATE_COLORS[state]} className="!mr-1.5">
                        {tn(`equipmentStates.${state}`)}
                      </Tag>
                      {itemLine(item)}
                      {item.service ? (
                        <>
                          {' '}
                          <Tooltip title={item.sentTo?.join(', ')}>
                            <span className="cursor-help text-[12px] text-zinc-500 underline decoration-dotted">
                              {tn('goesTo', { to: item.service })}
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        item.sentTo?.length > 0 && (
                          <span className="break-all text-[12px] text-zinc-500">
                            {' '}
                            {tn('goesTo', { to: item.sentTo.join(', ') })}
                          </span>
                        )
                      )}
                      {item.assignedAt && (
                        <StepLine
                          label={tn('assignedOn')}
                          date={item.assignedAt}
                          note={item.assignedNote}
                          byName={item.assignedByName}
                        />
                      )}
                      {item.returnedAt && (
                        <StepLine
                          label={tn('returnedOn')}
                          date={item.returnedAt}
                          note={item.returnedNote}
                          byName={item.returnedByName}
                        />
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      <ItemHistory history={item.history} tn={tn} />
                      {actions(request, item)}
                    </span>
                  </li>
                );
              })}
            </ul>
            {request.answers?.length > 0 && (
              <p className="m-0 text-[12.5px] text-zinc-600">{answersLine(request.answers, tn)}</p>
            )}
            {request.note && (
              <p className="m-0 whitespace-pre-line text-zinc-600">
                <span className="font-bold text-zinc-700">{tn('note')}:</span>{' '}
                {request.note}
              </p>
            )}
            {request.sentTo?.length > 0 && !routedPerItem(request) && (
              <span className="text-[12px] text-zinc-500">
                {tn('sentTo', { to: request.sentTo.join(', ') })}
              </span>
            )}
          </section>
        ))
      )}

      <EquipmentStepModal
        step={step}
        saving={move.isPending}
        onCancel={() => setStep(null)}
        onSave={({ date, note }) =>
          move.mutate({
            requestId: step.request._id,
            item: step.item.name,
            group: step.item.group || '',
            action: step.action,
            date,
            note,
          })
        }
      />
    </div>
  );
}
